// ความสำเร็จ (achievements) (docs/PLAN.md เฟส 3)
// ตรวจตอนจบแต่ละชีวิต จากชีวิตที่เพิ่งจบและสมุดสัตว์ (src/shared/dex.ts) เก็บในเบราว์เซอร์เหมือนสมุดสัตว์

import { GROUPS, isGroupEnabled } from "../data/groups";
import { THREATENED } from "../data/iucn";
import { stagesOf, summarize } from "../engine/life";
import type { LifeState } from "../engine/types";
import { dexList, type Dex } from "./dex";

export interface AchievementCtx {
  life: LifeState; // ชีวิตที่เพิ่งจบ
  dex: Dex; // สมุดสัตว์หลังบันทึกชีวิตนี้แล้ว
  bornCountry: string | null; // ประเทศที่เกิด (บนบก) จากแผนที่ ถ้าไม่มีแผนที่เป็น null
}

export interface AchievementDef {
  id: string;
  nameTh: string;
  description: string;
  hidden?: boolean; // ไม่บอกเงื่อนไขจนกว่าจะได้
  check: (ctx: AchievementCtx) => boolean;
  progress?: (dex: Dex) => [number, number]; // ความคืบหน้าที่ดูได้จากสมุดสัตว์
}

const ENABLED_GROUPS = GROUPS.filter(isGroupEnabled);
const SUBGROUP_TOTAL = ENABLED_GROUPS.reduce((n, g) => n + g.subgroups.length, 0);

const speciesCount = (dex: Dex) => dexList(dex).length;
const groupCount = (dex: Dex) => new Set(dexList(dex).map((e) => e.groupId)).size;
const subgroupCount = (dex: Dex) => new Set(dexList(dex).map((e) => e.subgroupId)).size;
const mostLives = (dex: Dex) => Math.max(0, ...dexList(dex).map((e) => e.lives));
const totalLives = (dex: Dex) => dexList(dex).reduce((n, e) => n + e.lives, 0);

const s = (ctx: AchievementCtx) => summarize(ctx.life);
const ownOffspring = (ctx: AchievementCtx) => (ctx.life.flags.includes("role_worker") ? 0 : ctx.life.offspring);

function collect(id: string, nameTh: string, description: string, count: (dex: Dex) => number, target: number): AchievementDef {
  return { id, nameTh, description, check: ({ dex }) => count(dex) >= target, progress: (dex) => [Math.min(count(dex), target), target] };
}

export const ACHIEVEMENTS: readonly AchievementDef[] = [
  // ---------- ชีวิตเดียว ----------
  { id: "first_life", nameTh: "ก้าวแรกของชีวิต", description: "เล่นจบหนึ่งชีวิต", check: () => true },
  { id: "adult", nameTh: "รอดจนโต", description: "มีชีวิตรอดจนเป็นตัวเต็มวัย", check: (c) => s(c).reachedAdult },
  { id: "old_age", nameTh: "อายุขัยเต็ม", description: "จากไปอย่างสงบเพราะอายุมาก", check: (c) => c.life.causeOfDeath === "old_age" },
  { id: "first_offspring", nameTh: "มีทายาท", description: "มีลูกของตัวเองอย่างน้อยหนึ่งตัว", check: (c) => ownOffspring(c) > 0 },
  { id: "big_family", nameTh: "ครอบครัวใหญ่", description: "มีลูก 100 ตัวขึ้นไปในชีวิตเดียว", check: (c) => ownOffspring(c) >= 100 },
  { id: "thousand_young", nameTh: "พันชีวิตใหม่", description: "มีลูก 1,000 ตัวขึ้นไปในชีวิตเดียว", check: (c) => ownOffspring(c) >= 1000 },
  { id: "helper", nameTh: "พี่เลี้ยงประจำรัง", description: "เป็นตัวงานที่ช่วยเลี้ยงน้อง 50 ตัวขึ้นไป", check: (c) => c.life.flags.includes("role_worker") && c.life.offspring >= 50 },
  { id: "queen", nameTh: "นางพญา", description: "เป็นนางพญาที่สร้างรังและวางไข่ได้", check: (c) => c.life.flags.includes("role_queen") && c.life.offspring > 0 },
  { id: "threatened_adult", nameTh: "ความหวังของเผ่าพันธุ์", description: "เป็นสัตว์ใกล้สูญพันธุ์ที่รอดจนโตเต็มวัย", check: (c) => c.life.species.iucn !== null && THREATENED.has(c.life.species.iucn) && s(c).reachedAdult },
  { id: "lineage_3", nameTh: "สามชั่วคน", description: "เล่นต่อเป็นลูกจนถึงรุ่นที่ 3", check: (c) => (c.life.lineage?.generation ?? 1) >= 3 },
  { id: "lineage_10", nameTh: "สายเลือดสิบรุ่น", description: "เล่นต่อเป็นลูกจนถึงรุ่นที่ 10", check: (c) => (c.life.lineage?.generation ?? 1) >= 10 },
  { id: "traveler", nameTh: "นักเดินทาง", description: "เดินทางรวมกว่า 1,000 กม. ในชีวิตเดียว", check: (c) => (c.life.geo?.km ?? 0) >= 1000 },
  { id: "globetrotter", nameTh: "ข้ามซีกโลก", description: "เดินทางรวมกว่า 10,000 กม. ในชีวิตเดียว", check: (c) => (c.life.geo?.km ?? 0) >= 10000 },
  { id: "migrant", nameTh: "ตามฤดูกาล", description: "อพยพไปอีกแหล่งตามฤดู", check: (c) => (c.life.migrations ?? 0) >= 1 },
  { id: "round_trip", nameTh: "ไปกลับครบรอบ", description: "อพยพไปและกลับในชีวิตเดียว", check: (c) => (c.life.migrations ?? 0) >= 2 },
  { id: "deep_sea", nameTh: "ใต้ทะเลลึก", description: "ลงไปลึกกว่า 1,000 เมตรใต้ผิวน้ำ", check: (c) => (c.life.geo?.lowest ?? 0) <= -1000 },
  { id: "high_ground", nameTh: "หลังคาโลก", description: "ขึ้นไปสูงกว่า 4,000 เมตรจากระดับน้ำทะเล", check: (c) => (c.life.geo?.highest ?? 0) >= 4000 },
  { id: "born_thai", nameTh: "ลูกหลานแผ่นดินไทย", description: "เกิดในประเทศไทย", check: (c) => c.bornCountry === "TH" },
  // ---------- ซ่อนไว้ ----------
  { id: "egg_end", nameTh: "ไม่ทันได้ลืมตา", description: "จบชีวิตตั้งแต่ยังเป็นไข่", hidden: true, check: (c) => c.life.stageIndex === 0 && stagesOf(c.life.species)[0].tag === "dormant" },
  { id: "named_hunter", nameTh: "อาหารของใครบางคน", description: "ถูกนักล่าที่รู้ชื่อชนิดจับกิน", hidden: true, check: (c) => !!c.life.killer },
  { id: "sacrifice", nameTh: "เพื่อครอบครัว", description: "สละชีวิตเพื่อปกป้องรังหรือลูก", hidden: true, check: (c) => c.life.causeOfDeath === "sacrifice" },
  {
    id: "last_stage",
    nameTh: "ครบทุกช่วงวัย",
    description: "อยู่จนถึงช่วงวัยสุดท้ายของชนิดที่มีตั้งแต่ 4 ช่วงวัยขึ้นไป",
    hidden: true,
    check: (c) => stagesOf(c.life.species).length >= 4 && c.life.stageIndex === stagesOf(c.life.species).length - 1,
  },
  // ---------- สะสม ----------
  collect("dex_5", "นักสะสมมือใหม่", "มีสัตว์ในสมุด 5 ชนิด", speciesCount, 5),
  collect("dex_25", "นักธรรมชาติวิทยา", "มีสัตว์ในสมุด 25 ชนิด", speciesCount, 25),
  collect("dex_100", "สารานุกรมเดินได้", "มีสัตว์ในสมุด 100 ชนิด", speciesCount, 100),
  collect("all_groups", "ครบทุกกลุ่ม", `เกิดเป็นสัตว์ครบทั้ง ${ENABLED_GROUPS.length} กลุ่ม`, groupCount, ENABLED_GROUPS.length),
  collect("half_subgroups", "ครึ่งทางของต้นไม้แห่งชีวิต", `เกิดเป็นสัตว์ครบ ${Math.ceil(SUBGROUP_TOTAL / 2)} กลุ่มย่อย`, subgroupCount, Math.ceil(SUBGROUP_TOTAL / 2)),
  collect("all_subgroups", "ต้นไม้แห่งชีวิต", `เกิดเป็นสัตว์ครบทั้ง ${SUBGROUP_TOTAL} กลุ่มย่อย`, subgroupCount, SUBGROUP_TOTAL),
  collect("same_species_5", "ผูกพันกันเป็นพิเศษ", "เล่นเป็นชนิดเดียวกัน 5 ชีวิต", mostLives, 5),
  collect("lives_50", "ห้าสิบชาติ", "เล่นจบทั้งหมด 50 ชีวิต", totalLives, 50),
];

export interface AchievementState {
  version: 1;
  unlocked: Record<string, string>; // id → วันที่ได้ YYYY-MM-DD
}

export const emptyAchievements = (): AchievementState => ({ version: 1, unlocked: {} });

/** ตรวจหลังจบชีวิต คืนสถานะใหม่และความสำเร็จที่เพิ่งได้ */
export function unlockAfterLife(state: AchievementState, ctx: AchievementCtx, now = new Date()): { state: AchievementState; newly: AchievementDef[] } {
  if (ctx.life.alive) return { state, newly: [] };
  const newly = ACHIEVEMENTS.filter((a) => !state.unlocked[a.id] && a.check(ctx));
  if (!newly.length) return { state, newly };
  const date = now.toISOString().slice(0, 10);
  const unlocked = { ...state.unlocked };
  for (const a of newly) unlocked[a.id] = date;
  return { state: { version: 1, unlocked }, newly };
}

export function parseAchievements(raw: string | null): AchievementState {
  if (!raw) return emptyAchievements();
  try {
    const data = JSON.parse(raw) as Partial<AchievementState>;
    if (data.version !== 1 || typeof data.unlocked !== "object" || data.unlocked === null) return emptyAchievements();
    const known = new Set(ACHIEVEMENTS.map((a) => a.id));
    const unlocked: Record<string, string> = {};
    for (const [id, date] of Object.entries(data.unlocked)) if (known.has(id) && typeof date === "string") unlocked[id] = date;
    return { version: 1, unlocked };
  } catch {
    return emptyAchievements();
  }
}
